import { useState, useEffect, useCallback } from 'react';
import { safeLocalStorage, STORAGE_KEYS } from '@/utils/encryption';

/**
 * Custom hook for managing dismissed perfect matches
 * Stores dismissed program IDs in localStorage and provides functions to manage them
 */
export const useDismissedMatches = () => {
  const [dismissedMatches, setDismissedMatches] = useState([]);

  // Load dismissed matches from localStorage on mount
  useEffect(() => {
    try {
      const saved = safeLocalStorage.getItem(STORAGE_KEYS.DISMISSED_MATCHES);
      if (saved && Array.isArray(saved)) {
        setDismissedMatches(saved);
      }
    } catch (error) {
      console.warn('Could not load dismissed matches:', error);
      setDismissedMatches([]);
    }
  }, []);

  // Dismiss a match (add to dismissed list)
  const dismissMatch = useCallback((programId) => {
    if (!programId) return;
    
    setDismissedMatches(prev => {
      // Avoid duplicates
      if (prev.includes(programId)) {
        return prev;
      }
      
      const updated = [...prev, programId];
      
      try {
        safeLocalStorage.setItem(STORAGE_KEYS.DISMISSED_MATCHES, updated);
      } catch (error) {
        console.warn('Could not save dismissed matches:', error);
      }
      
      return updated;
    });
  }, []);

  // Restore a match (remove from dismissed list)
  const restoreMatch = useCallback((programId) => {
    if (!programId) return;
    
    setDismissedMatches(prev => {
      const updated = prev.filter(id => id !== programId);
      
      try {
        safeLocalStorage.setItem(STORAGE_KEYS.DISMISSED_MATCHES, updated);
      } catch (error) {
        console.warn('Could not save dismissed matches:', error);
      }
      
      return updated;
    });
  }, []);

  // Check if a program is dismissed
  const isDismissed = useCallback((programId) => {
    if (!programId) return false;
    return dismissedMatches.includes(programId);
  }, [dismissedMatches]);

  // Get all dismissed match IDs
  const getDismissedMatches = useCallback(() => {
    return dismissedMatches;
  }, [dismissedMatches]);

  // Clear all dismissed matches
  const clearDismissedMatches = useCallback(() => {
    setDismissedMatches([]);
    try {
      safeLocalStorage.setItem(STORAGE_KEYS.DISMISSED_MATCHES, []);
    } catch (error) {
      console.warn('Could not clear dismissed matches:', error);
    }
  }, []);

  return {
    dismissedMatches,
    dismissMatch,
    restoreMatch,
    isDismissed,
    getDismissedMatches,
    clearDismissedMatches
  };
};

export default useDismissedMatches;
